(() => {
  class BlogSlider extends HTMLElement {
    constructor() {
      super();
  
      this.flkty = null;
      this.layoutFrame = null;
      this.initialized = false;
      this.mediaQuery = window.matchMedia('(max-width: 768px)');
  
      this.handleDocumentReady = this.init.bind(this);
      this.handleLayoutChange = this.scheduleLayout.bind(this);
      this.handleCarouselChange = this.updateNavState.bind(this);
      this.handlePrevClick = this.goToPreviousSlide.bind(this);
      this.handleNextClick = this.goToNextSlide.bind(this);
    }
  
    connectedCallback() {
      if (this.initialized) return;
  
      this.initialized = true;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.handleDocumentReady, { once: true });
      } else {
        this.init();
      }
    }
  
    disconnectedCallback() {
      this.destroy();
    }
  
    init() {
      this.sliderContainer = this.querySelector('.blog-slider-container');
      if (!this.sliderContainer) return;
  
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
  
      this.flkty = new Flickity(this.sliderContainer, {
        cellAlign: 'left',
        contain: true,
        wrapAround: false,
        pageDots: false,
        prevNextButtons: false,
        autoPlay: false,
        groupCells: true,
        imagesLoaded: true,
        adaptiveHeight: false,
        setGallerySize: true,
        draggable: this.mediaQuery.matches,
        on: {
          ready: () => this.scheduleLayout(),
        },
      });
  
      this.prevButton?.addEventListener('click', this.handlePrevClick);
      this.nextButton?.addEventListener('click', this.handleNextClick);
      this.flkty.on('change', this.handleCarouselChange);
      window.addEventListener('resize', this.handleLayoutChange);
      this.mediaQuery.addEventListener('change', this.handleLayoutChange);
  
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.scheduleLayout());
      }
  
      this.scheduleLayout();
    }
  
    destroy() {
      this.initialized = false;
      document.removeEventListener('DOMContentLoaded', this.handleDocumentReady);
  
      if (this.layoutFrame) {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = null;
      }
  
      this.prevButton?.removeEventListener('click', this.handlePrevClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      window.removeEventListener('resize', this.handleLayoutChange);
      this.mediaQuery.removeEventListener('change', this.handleLayoutChange);
  
      if (this.flkty) {
        this.flkty.off('change', this.handleCarouselChange);
        this.flkty.destroy();
        this.flkty = null;
      }
    }
  
    scheduleLayout() {
      if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
  
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        if (!this.isConnected || !this.flkty) return;
  
        this.applyMobileDrag();
        this.updateExcerptLineClamps();
        this.calculateHeights();
        this.updateNavState();
      });
    }
  
    calculateHeights() {
      const slides = this.querySelectorAll('.blog-slider__item');
      if (!slides.length) return;
  
      slides.forEach((slide) => {
        slide.style.height = 'auto';
      });
  
      const maxHeight = Math.max(...Array.from(slides, (slide) => slide.offsetHeight));
  
      slides.forEach((slide) => {
        slide.style.height = `${maxHeight}px`;
      });
  
      this.flkty?.resize();
    }
  
    getLineCount(element) {
      if (!element.offsetHeight) return 0;
      const style = getComputedStyle(element);
      let lineHeight = Number.parseFloat(style.lineHeight);
  
      if (Number.isNaN(lineHeight) || style.lineHeight === 'normal') {
        lineHeight = Number.parseFloat(style.fontSize) * 1.3;
      }
  
      return Math.max(1, Math.round(element.getBoundingClientRect().height / lineHeight));
    }
  
    getTotalLines() {
      const desktop = Number.parseInt(this.dataset.textLinesDesktop, 10) || 5;
      const mobile = Number.parseInt(this.dataset.textLinesMobile, 10) || 8;
  
      return this.mediaQuery.matches ? mobile : desktop;
    }
  
    updateExcerptLineClamps() {
      const totalLines = this.getTotalLines();
      const measurements = [];

      this.querySelectorAll('.blog-slider__item').forEach((item) => {
        const title = item.querySelector('.blog-slider__article-title');
        const excerpt = item.querySelector('.blog-slider__excerpt');
        if (!excerpt) return;

        const titleLines = title ? this.getLineCount(title) : 0;
        measurements.push({ excerpt, excerptLines: Math.max(0, totalLines - titleLines) });
      });

      // Read every title before changing excerpts, so cards share one layout pass.
      measurements.forEach(({ excerpt, excerptLines }) => {
        const hidden = excerptLines === 0;
        if (excerpt.classList.contains('blog-slider__excerpt--hidden') !== hidden) {
          excerpt.classList.toggle('blog-slider__excerpt--hidden', hidden);
        }

        const styles = {
          display: hidden ? '' : '-webkit-box',
          '-webkit-line-clamp': hidden ? '' : String(excerptLines),
        };
        if (!hidden) {
          styles['-webkit-box-orient'] = 'vertical';
          styles.overflow = 'hidden';
        }

        Object.entries(styles).forEach(([property, value]) => {
          if (excerpt.style.getPropertyValue(property) === value && !excerpt.style.getPropertyPriority(property)) return;
          if (value) {
            excerpt.style.setProperty(property, value);
          } else {
            excerpt.style.removeProperty(property);
          }
        });
      });
    }
  
    applyMobileDrag() {
      if (!this.flkty) return;
  
      this.flkty.options.draggable = this.mediaQuery.matches;
      this.flkty.updateDraggable();
    }
  
    goToPreviousSlide() {
      this.flkty?.previous();
    }
  
    goToNextSlide() {
      this.flkty?.next();
    }
  
    updateNavState() {
      if (!this.flkty) return;
  
      const selectedIndex = this.flkty.selectedIndex;
      const lastIndex = this.flkty.slides.length - 1;
  
      if (this.prevButton) {
        const isDisabled = selectedIndex === 0;
        this.prevButton.disabled = isDisabled;
        this.prevButton.setAttribute('aria-disabled', String(isDisabled));
      }
  
      if (this.nextButton) {
        const isDisabled = selectedIndex === lastIndex;
        this.nextButton.disabled = isDisabled;
        this.nextButton.setAttribute('aria-disabled', String(isDisabled));
      }
    }
  }
  
  if (!customElements.get('blog-slider')) {
    customElements.define('blog-slider', BlogSlider);
  }
})();

